// The lands (today: Bamboo Badlands; Meditation Mountain and Forest Pilgrimage are listed but greyed out). Each works exactly like Capital City: ask to travel →
// the travel screen wipes in with a real loading bar → the land is built (lazily, on the first visit) → the cat spawns there →
// RETURN HOME (or H) brings it back, settling the trip. While away, home is on its own: the same trip ledger, absence-risk raids,
// locked home panels and "welcome home" tally as the Capital (game/capitalTrip.js).
//
// A land's own art and rules live in src/scene/lands/<id>.js; this file is only the shared trip flow.
import * as THREE from 'three'
import { Events, on, emit } from '../ui/eventBus.js'
import { CapitalConfig } from '../config/capital.js'
import { Destinations, destinationById, LandPlaceholder } from '../config/destinations.js'
import * as advManager from '../game/adventureManager.js'
import { setAway, isAwayBusy } from '../game/awayState.js'
import { startTrip, endTrip } from '../game/capitalTrip.js'
import { setWalkRules, snapCameraToHero } from './heroController.js'
import { createAwayHome } from './awayHome.js'
import { buildLand } from './lands/index.js'
import { createLandMood } from './landMood.js'
import * as travel from '../ui/capitalTravel.js'

let sceneRef = null
let heroRef = null
let cameraRef = null
let rendererRef = null
const _sz = new THREE.Vector2()
let mood = null
let state = 'idle' // idle → travelling → active → leaving → idle
let currentId = null
const built = new Map() // id → { root, spawn, walk, tick? } (a land is built once, on its first visit)
let home = null
let savedHeroPos = null
let savedHeroRot = 0

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
const announce = () => {
  const p = { id: currentId, active: state === 'active', busy: state === 'travelling' || state === 'leaving' }
  emit(Events.LandChange, p)
  setAway(currentId, p)
}

export function createLandScene({ scene, hero, camera, ambient, sun, renderer }) {
  sceneRef = scene
  heroRef = hero
  cameraRef = camera ?? null
  rendererRef = renderer ?? null
  mood = ambient && sun ? createLandMood({ scene, ambient, sun }) : null // (each land's own atmosphere: mist, sky wash, colour grade)
  home = createAwayHome(() => sceneRef, () => heroRef)
  on(Events.VisitLand, ({ id, name, stayId }) => { visit(id, name, { stayId }) })
  on(Events.LeaveLand, () => { leave() })
  on(Events.AdventureStart, () => { if (state === 'active') leave({ instant: true }) }) // (the old timed adventure takes over: come home quietly)
  if (import.meta.env?.DEV) {
    window.__land = { info: () => built.get(currentId)?.info?.() ?? null, walk: (x, z) => built.get(currentId)?.walk?.(x, z) ?? null, teleport: (x, z) => { if (state !== 'active') return false; heroRef.position.set(x, heroRef.position.y, z); snapCameraToHero(); return true }, floorM: (mx, mz, my = null) => built.get(currentId)?.floorM?.(mx, mz, my) ?? null, visit, leave, state: () => state, id: () => currentId, spawn: () => built.get(currentId)?.spawn ?? null, ids: () => Destinations.filter((d) => d.id !== 'capital' && !d.hidden).map((d) => d.id),
      debug: () => { const b = built.get(currentId); if (!b) return null; const bx = new THREE.Box3().setFromObject(b.root); return { kind: b.kind ?? 'placeholder', instanced: b.instanced ?? 0, colliders: b.colliders ?? 0, nav: !!b.nav, visible: b.root.visible, inScene: sceneRef.children.includes(b.root), children: b.root.children.length, box: [bx.min.toArray(), bx.max.toArray()], hero: heroRef.position.toArray(), ground: b.root.children[0]?.material?.color?.getHexString?.(), night: document.body.className } } }
  }
}

async function visit(id, nameOverride = null, ctx = {}) { // (nameOverride: a guest's visit is named after the host: "Lord Whiskerton's kingdom")
  const def = destinationById[id]
  if (!def || def.id === 'capital' || !def.built || !sceneRef) return
  if (state !== 'idle' || isAwayBusy() || advManager.isAdventuring?.()) return
  currentId = id
  state = 'travelling'; announce()
  const t0 = performance.now()
  const title = nameOverride ?? def.name
  await travel.open({ title: `Travelling to ${title}`, cn: def.cn ?? LandPlaceholder.seal, tip: false })
  try {
    if (!built.has(id)) {
      const b = await buildLand(def, (p) => travel.setProgress(p), ctx)
      sceneRef.add(b.root)
      built.set(id, b)
    }
  } catch (err) {
    console.error(`[land] could not build ${def.name}:`, err)
    state = 'idle'; announce(); currentId = null
    await travel.fail()
    return
  }
  const wait = CapitalConfig.travelMinMs - (performance.now() - t0)
  if (wait > 0) await sleep(wait)
  swapIn(built.get(id), def, title)
  state = 'active'; announce()
  await travel.close()
}

async function leave({ instant = false } = {}) {
  if (state !== 'active') return
  const id = currentId
  state = 'leaving'; announce()
  if (!instant) await travel.open({ title: 'Returning home', cn: '归家', tip: false })
  const summary = endTrip() // settles the trip: gains and losses land in the treasury
  const gone = built.get(id)
  swapOut(gone)
  if (gone?.dispose) { gone.dispose(); built.delete(id) } // a visited kingdom is built fresh each time and freed when you go home
  document.body.classList.remove('in-land')
  state = 'idle'; announce(); currentId = null
  if (!instant) { await sleep(300); await travel.close() }
  if (summary) emit(Events.CapitalReturn, { summary }) // the "welcome home" tally (it names the place)
}

// Mountain ground rises well above the cat, and the iso camera sits just 17 units behind it: a negative near plane keeps tall ground that
// lies behind the camera in view instead of slicing it off.
const setCameraDepth = (near, far) => { if (cameraRef) { cameraRef.near = near; cameraRef.far = far; cameraRef.updateProjectionMatrix() } }

function swapIn(b, def, title = def.name) {
  home.hide()
  setCameraDepth(-160, 400)
  mood?.apply(b.mood)
  b.root.visible = true
  savedHeroPos = heroRef.position.clone()
  savedHeroRot = heroRef.rotation.y
  heroRef.position.set(b.spawn.x, b.spawn.y, b.spawn.z)
  heroRef.rotation.y = Math.PI
  setWalkRules(b.walk)
  snapCameraToHero()
  document.body.classList.add('in-land') // swaps the home HUD panels for the trip ledger, like the Capital
  startTrip(title)
}

function swapOut(b) {
  if (b) b.root.visible = false
  setCameraDepth(0.1, 100)
  mood?.clear()
  setWalkRules(null)
  home.restore()
  if (savedHeroPos) heroRef.position.copy(savedHeroPos)
  heroRef.rotation.y = savedHeroRot
  snapCameraToHero()
}

// Per frame: keep anything that appears at home while we're away hidden (raiders, effects…), and run the land's own tick.
export function tickLandScene(dt = 0, night = 0) {
  if (state !== 'active' || !sceneRef) return
  home.tick()
  const b = built.get(currentId)
  let px = 0 // screen pixels per world unit (the night glows are sized in pixels)
  if (cameraRef && rendererRef) { rendererRef.getDrawingBufferSize(_sz); px = _sz.y / ((cameraRef.top - cameraRef.bottom) / cameraRef.zoom) }
  b?.tick?.(dt, heroRef.position.x, heroRef.position.z, night, cameraRef ? { y: heroRef.position.y, cam: cameraRef.position, px } : null)
  mood?.tick(night) // (after the day/night cycle has set the lights this frame)
}
